// OWNER: FRONTEND-ADMIN. SPEC §15.8 Voicemails (recordings): my recordings, the in-browser recorder, and (admins)
// the review queue with the five-point approval checklist.
import { useState } from 'preact/hooks';
import type { Campaign, VmRecording } from '../../../src/shared/api';
import { errorMessage } from '../api';
import { Empty, ErrorBanner, fmtDateTime, toast } from '../components/ui';
import { useMe } from '../me';
import { adminApi, type RecordingReview } from '../admin/endpoints';
import { RECORDING_CHECKS, Recorder } from '../admin/Recorder';
import { MAX_DROP_SECONDS } from '../admin/wav';
import { Check, ConfirmModal, Field, StatusChip, Tabs, inputValue, useLoad } from '../admin/ui';
import { UNMEASURED_RECORDING, UNMEASURED_REJECT_NOTE, needsRejectForLength, recordingApproveBlockers } from '../admin/validate';

type TabKey = 'mine' | 'review' | 'all';

const deletedText = (r: { deleted: boolean }) => (r.deleted ? 'Recording deleted.' : 'A call already played it, so it was kept for the record and marked rejected.');

export function Recordings(_props: { params: Record<string, string> }) {
  const me = useMe();
  const isAdmin = me.role === 'admin';
  const recordings = useLoad(() => adminApi.recordings(), []);
  const campaigns = useLoad(() => adminApi.campaigns(), []);
  const settings = useLoad(() => adminApi.settings(), []);
  const [tab, setTab] = useState<TabKey>('mine');

  const all = recordings.data ?? [];
  const mine = all.filter((r) => r.userId === me.id);
  const pending = all.filter((r) => r.status === 'pending');
  const campaignName = (id: number | null) => (id === null ? 'Any of their campaigns' : campaigns.data?.find((c) => c.id === id)?.name ?? `Campaign #${id}`);

  const tabs: Array<{ key: TabKey; label: string; badge?: number | null }> = [{ key: 'mine', label: 'My recordings', badge: mine.length }];
  if (isAdmin) tabs.push({ key: 'review', label: 'Review queue', badge: pending.length }, { key: 'all', label: 'All recordings', badge: all.length });

  return (
    <div class="adm-page">
      <div class="adm-head">
        <div>
          <h1>Voicemail drops</h1>
          <div class="adm-sub">Pre-recorded voicemails in your own voice, used only where the law allows. Every recording is reviewed before it can be dropped.</div>
        </div>
      </div>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <ErrorBanner error={recordings.error} />
      <ErrorBanner error={campaigns.error} />

      {tab === 'mine' ? (
        <div class="adm-grid-2" style="align-items:start">
          <div class="stack">
            {settings.error ? <ErrorBanner error={`Couldn't load the callback number: ${settings.error}`} /> : null}
            {campaigns.data && settings.data ? (
              <Recorder campaigns={campaigns.data} callbackE164={settings.data.mainNumberE164} onUploaded={() => void recordings.reload()} />
            ) : campaigns.loading || settings.loading ? <div class="card muted">Loading the recorder…</div> : null}
          </div>
          <div class="stack">
            <h3>My recordings</h3>
            {recordings.data && mine.length === 0 ? <Empty>No recordings yet.</Empty> : null}
            {mine.map((r) => <RecordingCard r={r} campaignName={campaignName(r.campaignId)} canDelete onChanged={() => void recordings.reload()} />)}
          </div>
        </div>
      ) : null}

      {tab === 'review' && isAdmin ? (
        <div class="stack">
          {recordings.data && pending.length === 0 ? <Empty>Nothing waiting for review.</Empty> : null}
          {pending.map((r) => <ReviewCard r={r} campaignName={campaignName(r.campaignId)} onDone={() => void recordings.reload()} />)}
        </div>
      ) : null}

      {tab === 'all' && isAdmin ? <AllRecordings rows={all} campaigns={campaigns.data ?? []} onChanged={() => void recordings.reload()} /> : null}
    </div>
  );
}

function RecordingCard(props: { r: VmRecording; campaignName: string; canDelete: boolean; onChanged: () => void }) {
  const r = props.r;
  const [confirm, setConfirm] = useState(false);
  return (
    <div class="card stack">
      <div class="row" style="justify-content:space-between;flex-wrap:wrap">
        <div class="row"><b>{r.situation}</b><StatusChip status={r.status} /><span class="adm-sub">{props.campaignName}</span></div>
        <span class="adm-sub">{fmtDateTime(r.createdAt)}{r.durationSec !== null ? ` · ${r.durationSec.toFixed(1)} s` : ''}</span>
      </div>
      <audio controls preload="none" src={r.playUrl} style="width:100%" />
      <details><summary class="adm-sub" style="cursor:pointer">Script</summary><div style="white-space:pre-wrap;margin-top:6px">{r.scriptText}</div></details>
      {r.reviewNote ? <div class={`chip ${r.status === 'rejected' ? 'chip-bad' : 'chip-info'}`} style="display:block;padding:6px 10px">Reviewer: {r.reviewNote}</div> : null}
      {r.status === 'pending' && !r.reviewNote ? (
        <div class="chip chip-info" style="display:block;padding:6px 10px">Waiting for review. An admin listens to it before it can be dropped; it shows Approved here once it's cleared. Your own words are fine; it must open with your name and "Valistix" and give a callback number.</div>
      ) : null}
      {props.canDelete ? <div><button class="btn btn-sm" onClick={() => setConfirm(true)}>Delete</button></div> : null}
      {confirm ? (
        <ConfirmModal
          title="Delete this recording?"
          body={<span>If it was already used on a call it is kept for the record and marked rejected instead, so it can't be dropped again.</span>}
          confirmLabel="Delete" danger
          onConfirm={async () => { toast(deletedText(await adminApi.deleteRecording(r.id)), 'ok'); props.onChanged(); }}
          onClose={() => setConfirm(false)}
        />
      ) : null}
    </div>
  );
}

const CHECKLIST = RECORDING_CHECKS;

function ReviewCard(props: { r: VmRecording; campaignName: string; onDone: () => void }) {
  const r = props.r;
  const [checks, setChecks] = useState<RecordingReview['checklist']>({ legalNameFirst: false, callbackNumber: false, under30s: false, humanVoice: false, noClaims: false });
  // an unmeasured recording can only be rejected: start the note so one click sends it back
  const [note, setNote] = useState(r.durationSec === null ? UNMEASURED_REJECT_NOTE : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allChecked = CHECKLIST.every((c) => checks[c.key]);
  const blockers = recordingApproveBlockers(r, MAX_DROP_SECONDS);

  const submit = async (status: 'approved' | 'rejected') => {
    if (status === 'approved' && !allChecked) { setError('Every checklist item must be ticked to approve.'); return; }
    if (status === 'rejected' && !note.trim()) { setError('Write what the setter should fix so they can re-record.'); return; }
    setBusy(true); setError(null);
    try {
      await adminApi.reviewRecording(r.id, { status, reviewNote: note.trim(), checklist: checks });
      toast(status === 'approved' ? `Approved ${r.userName}'s “${r.situation}” recording.` : `Sent back to ${r.userName}.`, status === 'approved' ? 'ok' : 'warn');
      props.onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div class="card stack">
      <div class="row" style="justify-content:space-between;flex-wrap:wrap">
        <div class="row"><b>{r.userName}</b><span class="adm-sub">· {r.situation} · {props.campaignName}</span></div>
        <span class="adm-sub">{fmtDateTime(r.createdAt)} · {r.durationSec !== null ? `${r.durationSec.toFixed(1)} s` : 'length not measured'}</span>
      </div>
      {blockers.map((b) => <div class="chip chip-bad" style="display:block;padding:6px 10px;white-space:normal">{b}</div>)}
      <div class="adm-grid-2">
        <div class="stack">
          <audio controls preload="none" src={r.playUrl} style="width:100%" />
          <div class="eyebrow">Script they read</div>
          <div class="adm-script" style="font-size:14px">{r.scriptText}</div>
        </div>
        <div class="stack">
          <div class="eyebrow">Listen to the whole recording, then tick what's true</div>
          {CHECKLIST.map((c) => <Check checked={checks[c.key]} onChange={(v) => setChecks((prev) => ({ ...prev, [c.key]: v }))} label={c.label} />)}
          <Field label="Note (required to reject)"><textarea rows={2} value={note} onInput={(e) => setNote(inputValue(e))} placeholder="e.g. Say “Valistix” before your pitch; slow down on the number." /></Field>
          <ErrorBanner error={error} />
          <div class="row">
            <button class="btn btn-primary" disabled={busy || !allChecked || blockers.length > 0} title={blockers[0]} onClick={() => void submit('approved')}>Approve</button>
            <button class="btn btn-danger" disabled={busy} onClick={() => void submit('rejected')}>{r.durationSec === null ? 'Reject (length unknown)' : 'Reject'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AllRecordings(props: { rows: VmRecording[]; campaigns: Campaign[]; onChanged: () => void }) {
  const [status, setStatus] = useState('');
  const [user, setUser] = useState('');
  const users = [...new Map(props.rows.map((r) => [r.userId, r.userName])).entries()];
  const rows = props.rows.filter((r) => (!status || r.status === status) && (!user || String(r.userId) === user))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const [confirm, setConfirm] = useState<VmRecording | null>(null);
  const [rejecting, setRejecting] = useState<VmRecording | null>(null);
  const unmeasured = props.rows.filter(needsRejectForLength);
  return (
    <div class="stack">
      {unmeasured.length ? (
        <div class="chip chip-bad" style="display:block;padding:8px 10px;white-space:normal">
          {unmeasured.length === 1
            ? `1 recording has no measured length, so it can't be approved or dropped. Reject it so the setter records it again.`
            : `${unmeasured.length} recordings have no measured length, so they can't be approved or dropped. Reject them so the setters record them again.`}
        </div>
      ) : null}
      <div class="adm-toolbar">
        <select value={status} onChange={(e) => setStatus(inputValue(e))}>
          <option value="">Every status</option><option value="pending">pending</option><option value="approved">approved</option><option value="rejected">rejected</option>
        </select>
        <select value={user} onChange={(e) => setUser(inputValue(e))}>
          <option value="">Every setter</option>{users.map(([id, name]) => <option value={String(id)}>{name}</option>)}
        </select>
      </div>
      {rows.length === 0 ? <Empty>No recordings match.</Empty> : (
        <div class="adm-table-wrap">
          <table>
            <thead><tr><th>Setter</th><th>Situation</th><th>Campaign</th><th class="num">Length</th><th>Status</th><th>Review note</th><th>Created</th><th>Listen</th><th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr>
                <td>{r.userName}</td><td>{r.situation}</td>
                <td>{r.campaignId === null ? <span class="muted">any</span> : props.campaigns.find((c) => c.id === r.campaignId)?.name ?? `#${r.campaignId}`}</td>
                <td class="num">{r.durationSec !== null ? `${r.durationSec.toFixed(1)} s` : <span class="chip chip-bad" title={UNMEASURED_RECORDING}>not measured</span>}</td>
                <td><StatusChip status={r.status} /></td><td>{r.reviewNote ?? ''}</td><td>{fmtDateTime(r.createdAt)}</td>
                <td><audio controls preload="none" src={r.playUrl} style="height:32px;width:220px" /></td>
                <td style="white-space:nowrap">
                  {needsRejectForLength(r) ? <button class="btn btn-sm btn-danger" title={UNMEASURED_RECORDING} onClick={() => setRejecting(r)}>Reject…</button> : null}
                  {' '}<button class="btn btn-sm" onClick={() => setConfirm(r)}>Delete</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {confirm ? (
        <ConfirmModal
          title="Delete this recording?"
          body={<span>{confirm.userName}'s “{confirm.situation}” recording. If a call used it, it is kept and marked rejected instead.</span>}
          confirmLabel="Delete" danger
          onConfirm={async () => { toast(deletedText(await adminApi.deleteRecording(confirm.id)), 'ok'); props.onChanged(); }}
          onClose={() => setConfirm(null)}
        />
      ) : null}
      {rejecting ? (
        <ConfirmModal
          title="Reject this recording?"
          body={<span>{rejecting.userName}'s “{rejecting.situation}” recording ({rejecting.status}). {UNMEASURED_RECORDING}</span>}
          confirmLabel="Reject" danger
          noteLabel="Note for the setter" noteInitial={UNMEASURED_REJECT_NOTE} noteRequired
          onConfirm={async (note) => {
            await adminApi.reviewRecording(rejecting.id, {
              status: 'rejected', reviewNote: note,
              checklist: { legalNameFirst: false, callbackNumber: false, under30s: false, humanVoice: false, noClaims: false },
            });
            toast(`Sent back to ${rejecting.userName}.`, 'warn');
            props.onChanged();
          }}
          onClose={() => setRejecting(null)}
        />
      ) : null}
    </div>
  );
}
